import { useState, useEffect } from 'react';
import { subscribeToPresence } from '../services/presenceService';

// Module-level cache so presence data persists across component mounts/unmounts.
// When the user navigates between /chat and /chat/:id the status stays stable
// instead of resetting to 'offline' while the new subscription initialises.
const presenceCache = new Map();

/**
 * Subscribes to a user's live online/offline presence.
 *
 * @param {string} uid - The UID to watch. If null/undefined, returns offline.
 * @returns {{ state: 'online'|'offline', last_changed: number|null }}
 */
export function usePresence(uid) {
  const [presence, setPresence] = useState(() => {
    // Seed from cache so the UI shows the last known status instantly
    return presenceCache.get(uid) ?? { state: 'offline', last_changed: null };
  });

  useEffect(() => {
    if (!uid) return;

    // If we already have a cached value, apply it synchronously
    if (presenceCache.has(uid)) {
      setPresence(presenceCache.get(uid));
    }

    const unsubscribe = subscribeToPresence(uid, (data) => {
      presenceCache.set(uid, data);
      setPresence(data);
    });

    return () => unsubscribe();
  }, [uid]);

  return presence;
}

